"use client";

import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useOnborda } from "onborda";
import { useEffect, useState, useCallback } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "@/lib/i18n";
import * as m from "@/paraglide/messages";
import { createDemo } from "@/services/controllers/dashboard";
import { getConnectedProviders } from "@/services/controllers/exchanges";
import { completeTour } from "@/services/controllers/user";
import { useAppSelector } from "@/services/hooks/redux.hooks";

import LoadingSpinner from "./loading-spinner/loading-spinner";

type ThemeOption = {
  theme: string;
  icon: typeof Sun | typeof Moon;
  label: string;
};

const themeOptions: ThemeOption[] = [
  { theme: "light", icon: Sun, label: "Light Mode" },
  { theme: "dark", icon: Moon, label: "Dark Mode" },
];

export function WelcomeModal() {
  const { completed_onboarding } = useAppSelector((state) => state.user);
  const { startOnborda } = useOnborda();
  const { setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (completed_onboarding != null) {
      const shouldShow = !completed_onboarding?.includes("dashboard");
      setIsOpen(shouldShow);
    }
  }, [completed_onboarding]);

  const handleClose = useCallback((markAsDone: boolean) => {
    setIsOpen(false);
    if (markAsDone) {
      completeTour("dashboard");
    }
  }, []);

  const handleThemeChange = useCallback(
    (theme: string) => {
      setTheme(theme);
    },
    [setTheme],
  );

  const handleTourStart = useCallback(
    (tourName: string) => {
      handleClose(false);
      startOnborda(tourName);
    },
    [handleClose, startOnborda],
  );

  const onCreateDemo = useCallback(() => {
    setIsCreating(true);
    createDemo()
      .then(() => {
        getConnectedProviders({}).then(() => {
          handleTourStart("dashboard");
          setIsCreating(false);
          setIsOpen(false);
        });
      })
      .catch(() => {
        setIsCreating(false);
      });
  }, [handleTourStart]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose(true)}>
      <DialogContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        className="flex flex-col lg:max-w-96 max-w-xl"
      >
        <DialogHeader className="max-h-fit md:mt-0 mt-2">
          <DialogTitle className="text-center">
            {m.topical_short_fox_cook()}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto md:py-4 py-2 md:max-h-[50vh] max-h-[80vh]">
          <p className="text-center">{m.spry_vexed_zebra_forgive()}</p>

          <div className="flex flex-col space-y-4 mt-4">
            <p className="text-sm text-center">{m.only_careful_crab_scoop()}</p>

            <div className="flex justify-center space-x-4">
              {themeOptions.map(({ theme, icon: Icon, label }) => (
                <Button
                  key={theme}
                  variant="outline"
                  size="lg"
                  onClick={() => handleThemeChange(theme)}
                  className="flex w-full items-center space-x-2 px-4 py-2"
                >
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </Button>
              ))}
            </div>

            <p className="text-sm mt-4 text-center">
              {m.quiet_new_trout_bask()}
            </p>

            <div className="w-full flex flex-col items-center space-y-1">
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={onCreateDemo}
              >
                {isCreating ? <LoadingSpinner /> : m.that_such_lobster_bend()}
              </Button>

              <Link href="" onClick={() => handleClose(true)}>
                <p className="text-xs italic text-foreground underline">
                  {m.chunky_early_iguana_pat()}
                </p>
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
